/**
 * Esportazione piano turni in PDF A4 orizzontale.
 * Formato tabella Piano Turni: colori delle celle calcolati come in `styles/main.css` (`.shift-table-grid`), con `color-mix`/`#RRGGBBAA` rispetto a `#0f1419`; colonna nominativo come `--bg-tertiary`.
 */

import { getState, getAssignmentByKey, getCreditedAbsenceHoursForRange, getEffectiveAbsences, isWeekLocked, getBimesterSnapshots } from './store.js';
import { getOfficialSlotsForDates } from './shiftGenerator.js';
import {
  getHoursForSlot,
  ABSENCE_CATEGORY_ABBR,
  ABSENCE_CATEGORIES,
  HOURS_PER_DAY,
} from './types.js';
import {
  getPlannerTableRowLabelKey,
  getSlotTypeRowLabel,
  getSlotTypesForWeek,
  getTableSectionSortKey,
  isAnesthesiaAmbulatorySlotType,
  isNoraLikeSalaSlotType,
} from './plannerTableLayout.js';
import { isSoMedicinaDoloreSlot } from './types.js';
import {
  getDaysInMonth,
  getDaysInWeekRange,
  getMonthYearFromStr,
  formatDateIt,
  getISOWeekString,
  getMondayOfISOWeek,
  stableCalendarSlotKey,
  slotLogicalKeyString,
} from './utils.js';
import {
  getWeekStart,
  computeWeeklyBreakdownFromSlots,
  computeBimesterMirror,
  countFerialiInDateRange,
  findWorkloadGaps,
  doctorsForWorkloadBalance,
} from './workload.js';
import { getAbsenceCalendarCellLabel } from './absenceCellLabel.js';

/** Abbreviazioni giorno: L, M, M, G, V, S, D */
const WEEKDAY_LETTER = { lun: 'L', mar: 'M', mer: 'M', gio: 'G', ven: 'V', sab: 'S', dom: 'D' };

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Evidenziazione celle turni del medico loggato (RGB) */
const MY_SHIFT_HIGHLIGHT = [255, 228, 120];
/** Evidenziazione turni modificati (post-rilascio): arancione-rosso tramonto, distinta dal giallo “miei turni”. */
const CHANGED_SHIFT_HIGHLIGHT = [235, 88, 62];
const MY_SHIFT_BORDER = [200, 130, 0];

/**
 * Il diff da lock_week usa `shift_slots.id` (DB); in PDF gli slot passano da getOfficialSlotsForDates e possono
 * avere id/slotKey stabile (-lgk-…). Allinea UUID ↔ chiavi persistite + chiave logica per evidenziare le celle giuste.
 */
function buildPdfChangedSlotMarkers(rawArr, shiftSlots) {
  const raw = (Array.isArray(rawArr) ? rawArr : [rawArr]).map((x) => String(x || '').trim()).filter(Boolean);
  if (!raw.length) return null;
  const keySet = new Set(raw);
  const logicalSet = new Set();
  let grew = true;
  while (grew) {
    grew = false;
    for (const s of shiftSlots || []) {
      if (!s?.date) continue;
      const id = String(s.id || '').trim();
      const sk = String(s.slotKey || '').trim();
      const stab = String(stableCalendarSlotKey(s) || '').trim();
      const hit = [id, sk, stab].filter(Boolean).some((k) => keySet.has(k));
      if (!hit) continue;
      const lk = slotLogicalKeyString(s);
      if (lk) logicalSet.add(lk);
      for (const k of [id, sk, stab]) {
        if (k && !keySet.has(k)) {
          keySet.add(k);
          grew = true;
        }
      }
    }
  }
  return { keySet, logicalSet };
}

function pdfSlotMatchesChangedMarkers(s, markers) {
  if (!markers || !s) return false;
  const idk = String(s.slotKey || s.id || '').trim();
  const stab = String(stableCalendarSlotKey(s) || '').trim();
  const lk = slotLogicalKeyString(s);
  if (idk && markers.keySet.has(idk)) return true;
  if (stab && markers.keySet.has(stab)) return true;
  if (lk && markers.logicalSet.has(lk)) return true;
  return false;
}

function blendRgb(base, tint, amount = 0.5) {
  return [
    Math.min(255, Math.round(base[0] * (1 - amount) + tint[0] * amount)),
    Math.min(255, Math.round(base[1] * (1 - amount) + tint[1] * amount)),
    Math.min(255, Math.round(base[2] * (1 - amount) + tint[2] * amount)),
  ];
}

/** True se almeno uno degli slot in `slots` è assegnato a doctorId */
function slotsAssignedToDoctor(slots, doctorId, assignmentsByKey) {
  if (!doctorId || !slots?.length) return false;
  return slots.some((s) => {
    const key = s.slotKey || s.id;
    return (assignmentsByKey[key] ?? getAssignmentByKey(key)) === doctorId;
  });
}

const MONTH_NAMES = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

/** Intestazioni sezione + colonna weekend (come tabella Piano Turni) */
const PDF_TABLE_THEME = {
  areaHeader: [74, 85, 104],
  weekendBlend: [248, 250, 252],
  defaultCell: [255, 255, 255],
};

/** `:root --bg-primary` (stesso valore della griglia Piano Turni in main.css). */
const PLANNER_THEME_BG_PRIMARY = Object.freeze([15, 20, 25]);

function mixPlannerPasteWithBg(r, g, b, pctHex, pctBg = 100 - pctHex) {
  const w1 = pctHex / 100;
  const w2 = pctBg / 100;
  const [br, bg, bb] = PLANNER_THEME_BG_PRIMARY;
  return [
    Math.min(255, Math.round(r * w1 + br * w2)),
    Math.min(255, Math.round(g * w1 + bg * w2)),
    Math.min(255, Math.round(b * w1 + bb * w2)),
  ];
}

/** Colore `#RRGGBB` compositato sopra uno sfondo RGB con alpha aaHex/255 (es. `#1d9bf028` → hex `1d9bf0`, aa `0x28`). */
function plannerHexWithAlphaOverlayOnBg(rrggbbHex, aaHex = 0xff, bgRgb = PLANNER_THEME_BG_PRIMARY) {
  const n = parseInt(rrggbbHex, 16);
  const rr = (n >> 16) & 0xff;
  const gg = (n >> 8) & 0xff;
  const bb = n & 0xff;
  const a = aaHex / 255;
  return [
    Math.min(255, Math.round(rr * a + bgRgb[0] * (1 - a))),
    Math.min(255, Math.round(gg * a + bgRgb[1] * (1 - a))),
    Math.min(255, Math.round(bb * a + bgRgb[2] * (1 - a))),
  ];
}

/** Luminanza per contrasto testo sullo sfondo (stessa logica intuitiva delle celle scure TI-notte/rep in UI). */
function plannerPdfTextRgbForFill(bgRgb, highlightWarm = false) {
  const lum = (0.2126 * bgRgb[0] + 0.7152 * bgRgb[1] + 0.0722 * bgRgb[2]) / 255;
  const darkInk = highlightWarm ? [30, 20, 0] : [20, 20, 20];
  if (lum < 0.45) return [245, 245, 247];
  return darkInk;
}

/** Stessi pastel + mix di `.shift-table-grid .grid-cell.cat-bg-*` in main.css (color-mix con --bg-primary). */
function plannerShiftGridCellRgb(slotLike) {
  const cat = String(slotLike.category || '').trim().toLowerCase();
  const ts = String(slotLike.timeSlot || '').trim().toLowerCase();
  const at = String(slotLike.activityType || 'clinico').trim().toLowerCase();
  if (at === 'reperibilita' || ts.startsWith('reperibilita')) {
    return plannerHexWithAlphaOverlayOnBg('00ba7c', 0x28);
  }
  if (cat === 'terapia_intensiva' && ts === 'notte') {
    return plannerHexWithAlphaOverlayOnBg('1d9bf0', 0x28);
  }
  if (cat === 'terapia_intensiva') {
    return mixPlannerPasteWithBg(224, 242, 254, 82, 18);
  }
  const stTyped = slotLike;
  if (isSoMedicinaDoloreSlot(stTyped)) {
    return mixPlannerPasteWithBg(254, 243, 199, 82, 18);
  }
  if (cat === 'sala_operatoria') {
    if (isAnesthesiaAmbulatorySlotType(stTyped)) {
      return mixPlannerPasteWithBg(209, 213, 219, 78, 22);
    }
    if (isNoraLikeSalaSlotType(stTyped)) {
      return mixPlannerPasteWithBg(253, 186, 116, 72, 28);
    }
    return mixPlannerPasteWithBg(209, 250, 229, 82, 18);
  }
  if (cat === 'nora') {
    return mixPlannerPasteWithBg(253, 186, 116, 72, 28);
  }
  if (cat === 'medicina_dolore') {
    return mixPlannerPasteWithBg(254, 243, 199, 82, 18);
  }
  if (cat === 'cure_palliative') {
    return mixPlannerPasteWithBg(255, 237, 213, 82, 18);
  }
  if (cat === 'formazione') {
    return mixPlannerPasteWithBg(224, 231, 255, 82, 18);
  }
  if (cat === 'direzione') {
    return mixPlannerPasteWithBg(237, 233, 254, 82, 18);
  }
  return [255, 255, 255];
}

function plannerPdfBannerKey(slotTypeLike) {
  const sk = getTableSectionSortKey(slotTypeLike);
  return `${sk.section}-${sk.subSection ?? 0}`;
}

function plannerPdfBannerTitle(slotTypeLike) {
  const sk = getTableSectionSortKey(slotTypeLike);
  const sub = sk.subSection || 0;
  switch (sk.section) {
    case 0:
      return 'Terapia intensiva';
    case 1:
      if (sub === 1) return 'Ambulatorio anestesia';
      if (sub === 2) return 'NORA';
      return 'Sala operatoria';
    case 2:
      return 'NORA';
    case 3:
      return 'Formazione';
    case 4:
      return 'Reperibilità';
    case 5:
      return 'Cure palliative';
    case 6:
      return 'Medicina del dolore';
    case 7:
      return 'Direzione';
    default:
      return 'Altro';
  }
}

function computeDoctorHours(slots, assignmentsByKey, dateRangeStart, dateRangeEnd, doctors) {
  const hours = {};
  const isRep = (s) => s.activityType === 'reperibilita' || s.timeSlot === 'reperibilita_notte' || s.timeSlot === 'reperibilita_giorno';
  for (const slot of slots) {
    const key = slot.slotKey || slot.id;
    const docId = assignmentsByKey[key];
    if (!docId) continue;
    if (!hours[docId]) hours[docId] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0 };
    const h = slot.hours ?? getHoursForSlot(slot.timeSlot);
    if (isRep(slot)) {
      hours[docId].reperibilita += h;
    } else {
      const isWeekend = ['sab', 'dom'].includes(slot.weekday);
      const isNight = slot.timeSlot === 'notte';
      if (isNight) hours[docId].notturne += h;   // tutte le notti
      if (isWeekend) hours[docId].weekend += h;   // tutto il weekend (M/P + notte)
      if (!isWeekend && !isNight) hours[docId].feriali += h;
    }
  }
  if (dateRangeStart && dateRangeEnd && doctors) {
    for (const doc of doctors) {
      if (!hours[doc.id]) hours[doc.id] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0 };
      const cred = getCreditedAbsenceHoursForRange(doc.id, dateRangeStart, dateRangeEnd);
      hours[doc.id].feriali += cred;
    }
  }
  return hours;
}

export async function loadJsPDF() {
  if (window.jspdfLoaded) return window.jspdfLoaded;
  const JsPDF = window.jspdf?.jsPDF || window.jsPDF;
  if (JsPDF) {
    window.jspdfLoaded = { jsPDF: JsPDF, autoTable: true };
    return window.jspdfLoaded;
  }

  const loadScript = (src) => new Promise((resolve, reject) => {
    const existing = [...document.scripts].find((s) => s.src === src);
    if (existing) {
      if (existing.dataset.loaded === '1') return resolve(true);
      existing.addEventListener('load', () => resolve(true), { once: true });
      existing.addEventListener('error', () => reject(new Error(src)), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => {
      s.dataset.loaded = '1';
      resolve(true);
    };
    s.onerror = () => reject(new Error(src));
    document.head.appendChild(s);
  });

  const jsPdfSources = [
    'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js',
    'https://unpkg.com/jspdf@2.5.1/dist/jspdf.umd.min.js',
  ];
  const autoTableSources = [
    'https://cdn.jsdelivr.net/npm/jspdf-autotable@3.5.31/dist/jspdf.plugin.autotable.min.js',
    'https://unpkg.com/jspdf-autotable@3.5.31/dist/jspdf.plugin.autotable.min.js',
  ];

  const tryLoadAny = async (sources, label) => {
    const errors = [];
    for (const src of sources) {
      try {
        await loadScript(src);
        return src;
      } catch (e) {
        errors.push(src);
      }
    }
    throw new Error(`${label} non raggiungibile (${errors.join(', ')})`);
  };

  await tryLoadAny(jsPdfSources, 'jsPDF');
  await tryLoadAny(autoTableSources, 'jsPDF-AutoTable');

  const jsPdfAfter = window.jspdf?.jsPDF || window.jsPDF;
  if (!jsPdfAfter) {
    throw new Error('Libreria jsPDF caricata ma non inizializzata correttamente');
  }
  window.jspdfLoaded = { jsPDF: jsPdfAfter, autoTable: true };
  return window.jspdfLoaded;
}

/** Restituisce sigle medici per slot (solo sigla, multiple separate da spazio) */
function getSigleForSlots(slots, doctors, assignmentsByKey) {
  if (!slots || slots.length === 0) return '—';
  const sigle = slots.map(s => {
    const key = s.slotKey || s.id;
    const docId = assignmentsByKey[key] ?? getAssignmentByKey(key);
    const doc = docId ? doctors.find(d => d.id === docId) : null;
    return doc ? doc.sigla : '—';
  });
  return [...new Set(sigle)].filter(s => s !== '—').join(' ') || '—';
}

export async function exportShiftPlanToPDF(options = {}) {
  try {
    const shouldDownload = options.download !== false;
    const includeBlob = options.returnBlob === true;
    const { jsPDF } = await loadJsPDF();
    const state = getState();
    const { doctors, shiftAssignmentsByKey, shiftPlanWeekFrom, shiftPlanWeekTo, currentMonth } = state;
    let days, slotsFromCalendar, titleRange, filenameBase, periodStart = null, periodEnd = null;
    const overrideWeekFrom = String(options.weekFrom || '').trim();
    const overrideWeekTo = String(options.weekTo || '').trim();
    if ((overrideWeekFrom && overrideWeekTo) || (shiftPlanWeekFrom && shiftPlanWeekTo)) {
      // getDaysInWeekRange richiede due LUNEDÌ: se dateTo è la domenica (es. da API), normalizza con getWeekStart.
      const weekFrom = (overrideWeekFrom && overrideWeekTo)
        ? getWeekStart(overrideWeekFrom)
        : shiftPlanWeekFrom;
      const weekTo = (overrideWeekFrom && overrideWeekTo)
        ? getWeekStart(overrideWeekTo)
        : shiftPlanWeekTo;
      days = getDaysInWeekRange(weekFrom, weekTo);
      const isUserRole = (state.currentUser?.role || '') === 'user';
      if (isUserRole) {
        days = days.filter(d => isWeekLocked(getWeekStart(d.date)));
        if (days.length === 0) {
          return { ok: false, error: 'Nessuna settimana archiviata nell\'intervallo selezionato. Seleziona un intervallo che includa settimane bloccate (con lucchetto).' };
        }
      }
      const dates = days.map(d => d.date);
      slotsFromCalendar = getOfficialSlotsForDates(dates);
      const first = days[0]?.date || '';
      const last = days[days.length - 1]?.date || '';
      periodStart = first;
      periodEnd = last;
      titleRange = `${formatDateIt(first)} – ${formatDateIt(last)}`;
      filenameBase = `turni_${formatDateIt(first)}_${formatDateIt(last)}`;
    } else {
      const { year, month } = getMonthYearFromStr(currentMonth);
      days = getDaysInMonth(year, month);
      const isUserRole = (state.currentUser?.role || '') === 'user';
      if (isUserRole) {
        days = days.filter(d => isWeekLocked(getWeekStart(d.date)));
        if (days.length === 0) {
          return { ok: false, error: 'Nessuna settimana archiviata in questo mese.' };
        }
      }
      slotsFromCalendar = getOfficialSlotsForDates(days.map((d) => d.date));
      periodStart = days[0]?.date || null;
      periodEnd = days[days.length - 1]?.date || null;
      titleRange = `${MONTH_NAMES[month - 1]} ${year}`;
      filenameBase = `turni_${year}_${String(month).padStart(2, '0')}`;
    }

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });
    const buildVersion = (() => {
      const el = document.getElementById('app-version');
      const txt = (el?.textContent || '').trim();
      return txt || 'build n/d';
    })();
    const generatedAt = (() => {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const hh = String(now.getHours()).padStart(2, '0');
      const mi = String(now.getMinutes()).padStart(2, '0');
      const ss = String(now.getSeconds()).padStart(2, '0');
      return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
    })();

    const pageW = 297;
    const pageH = 210;
    const margin = 12;
    const contentW = pageW - margin * 2;
    const WEEKS_PER_PAGE = 4;
    const cellPadding = 0.35;
    const baseFontSize = 9;
    const textMeasureCache = new Map();

    const measureTextMm = (text, fontSize) => {
      const key = `${fontSize}|${String(text ?? '')}`;
      if (textMeasureCache.has(key)) return textMeasureCache.get(key);
      const prev = doc.getFontSize();
      doc.setFontSize(fontSize);
      const lines = String(text ?? '').split('\n');
      let maxW = 0;
      for (const line of lines) {
        const w = doc.getTextWidth(line || ' ');
        if (w > maxW) maxW = w;
      }
      doc.setFontSize(prev);
      textMeasureCache.set(key, maxW);
      return maxW;
    };

    /**
     * Calcola larghezze colonna adattive in base al contenuto (header + body),
     * scalando per rientrare nella larghezza disponibile.
     */
    const getAdaptiveTableLayout = ({
      headRow,
      bodyRows,
      fontSize,
      cellPaddingMm,
      availableWidthMm,
      minWidthByCol = {},
      maxWidthByCol = {},
    }) => {
      const nCols = headRow.length;
      const widths = Array(nCols).fill(0);
      for (let c = 0; c < nCols; c++) {
        let maxTextW = measureTextMm(headRow[c], fontSize);
        for (const row of bodyRows) {
          const w = measureTextMm(row[c], fontSize);
          if (w > maxTextW) maxTextW = w;
        }
        const minW = minWidthByCol[c] ?? 6;
        const maxW = maxWidthByCol[c] ?? 999;
        widths[c] = Math.max(minW, Math.min(maxW, maxTextW + cellPaddingMm * 2 + 1.2));
      }

      let totalW = widths.reduce((a, b) => a + b, 0);
      if (totalW > availableWidthMm && totalW > 0) {
        const scale = availableWidthMm / totalW;
        for (let c = 0; c < nCols; c++) widths[c] = Math.max(4.5, widths[c] * scale);
        totalW = widths.reduce((a, b) => a + b, 0);
      }

      const columnStyles = {};
      for (let c = 0; c < nCols; c++) {
        columnStyles[c] = { cellWidth: widths[c], fontSize };
      }
      return { columnStyles, tableWidth: totalW };
    };

    // Slot map: date -> [slots]
    const slotMapByDate = {};
    const festiviByDate = new Set();
    slotsFromCalendar.forEach(s => {
      if (!slotMapByDate[s.date]) slotMapByDate[s.date] = [];
      slotMapByDate[s.date].push(s);
      if (s.isFestivoInfrasettimanale) festiviByDate.add(s.date);
    });

    // Righe PDF: stesso ordinamento della tabella Piano Turni (nome + fascia, sezioni TI/SO/NORA…)
    const slotTypes = getSlotTypesForWeek(slotsFromCalendar);

    const assignmentsByKey = shiftAssignmentsByKey || {};
    const highlightDoctorId = Object.prototype.hasOwnProperty.call(options, 'highlightDoctorId')
      ? (options.highlightDoctorId || null)
      : (state.currentUser?.doctorId || null);
    const changedMarkers = (() => {
      const raw = options.changedSlotKeys;
      if (!raw) return null;
      const arr = Array.isArray(raw) ? raw : [raw];
      return buildPdfChangedSlotMarkers(arr, state.shiftSlots || []);
    })();
    const highlightedDoctor = highlightDoctorId
      ? doctors.find((d) => d.id === highlightDoctorId)
      : null;
    let lastSlotTableBottom = 0;

    // Raggruppa giorni per settimana (lun-dom) e spezza in blocchi da 4 settimane
    const weekStarts = [...new Set(days.map(d => getWeekStart(d.date)))].sort();
    const dayChunks = [];
    for (let i = 0; i < weekStarts.length; i += WEEKS_PER_PAGE) {
      const chunkWeeks = weekStarts.slice(i, i + WEEKS_PER_PAGE);
      const chunkDays = days.filter(d => chunkWeeks.includes(getWeekStart(d.date)));
      dayChunks.push(chunkDays);
    }

    const renderTableForDays = (chunkDays, isFirstPage) => {
      let startY = isFirstPage ? 15 : margin;
      if (!isFirstPage) {
        doc.addPage('a4', 'l');
      }

      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Pianificazione Turni UOC - ATISI', margin, startY);
      startY += 6;
      doc.setFontSize(14);
      doc.setFont(undefined, 'normal');
      const firstDate = chunkDays[0]?.date || '';
      const lastDate = chunkDays[chunkDays.length - 1]?.date || '';
      doc.text(`Piano Turni - ${formatDateIt(firstDate)} – ${formatDateIt(lastDate)}`, margin, startY);
      startY += 5;
      if (highlightDoctorId) {
        doc.setFontSize(12);
        doc.setTextColor(90);
        doc.text(
          highlightedDoctor
            ? `Sfondo giallo: turni assegnati a ${highlightedDoctor.sigla} ${highlightedDoctor.cognome}.`
            : 'Sfondo giallo: turni del medico evidenziato.',
          margin,
          startY
        );
        startY += 4;
        if (changedMarkers) {
          doc.text(
            'Arancione-rosso e bordo scuro: turni modificati rispetto alla versione precedente.',
            margin,
            startY
          );
          startY += 4;
        }
        doc.setTextColor(0);
        doc.setFontSize(14);
      } else {
        startY += 3;
      }

      const weekOrder = [];
      const weekCounts = {};
      for (const d of chunkDays) {
        const ws = getWeekStart(d.date);
        if (!weekCounts[ws]) {
          weekOrder.push(ws);
          weekCounts[ws] = 0;
        }
        weekCounts[ws]++;
      }
      const isoWeekRow = [{ content: '', colSpan: 1 }];
      for (const ws of weekOrder) {
        const monthIdx = Math.max(0, parseInt((ws || '').slice(5, 7), 10) - 1);
        const monthTag = (MONTH_NAMES[monthIdx] || '').slice(0, 3).toUpperCase();
        isoWeekRow.push({ content: `${getISOWeekString(ws)}-${monthTag}`, colSpan: weekCounts[ws] });
      }
      const headRow = [
        '',
        ...chunkDays.map((d) => {
          const dayTxt = (d.date || '').slice(8, 10) || String(d.day || '').padStart(2, '0');
          return `${WEEKDAY_LETTER[d.weekday] || d.weekday}\n${dayTxt}`;
        }),
      ];
      const body = [];
      const rowTypes = [];
      const bodyRowMeta = [];
      let lastPlannerBannerKey = null;

      for (const st of slotTypes) {
        const bannerKey = plannerPdfBannerKey(st);
        if (bannerKey !== lastPlannerBannerKey) {
          body.push([plannerPdfBannerTitle(st), ...Array(chunkDays.length).fill('')]);
          rowTypes.push('areaHeader');
          bodyRowMeta.push({ kind: 'header' });
          lastPlannerBannerKey = bannerKey;
        }
        const { name, time } = getSlotTypeRowLabel(st);
        const rowLabel = time ? `${name} ${time}` : name;

        const row = [rowLabel];
        for (const d of chunkDays) {
          const slots = (slotMapByDate[d.date] || []).filter(
            (s) => getPlannerTableRowLabelKey(s) === st.key,
          );
          row.push(getSigleForSlots(slots, doctors, assignmentsByKey));
        }
        body.push(row);
        rowTypes.push('slotRow');
        bodyRowMeta.push({
          kind: 'slot',
          labelKey: st.key,
          category: st.category,
          name: st.name,
          timeSlot: st.timeSlot,
          activityType: st.activityType,
          uocId: st.uocId || '',
        });
      }

      const colLabelW = 44;
      const FIXED_COL_DAY_WIDTH = 8; // 4 settimane ISO (max 28 giorni) restano in una pagina A4 landscape
      const nDays = chunkDays.length;
      const colDayW = FIXED_COL_DAY_WIDTH;

      const columnStyles = { 0: { cellWidth: colLabelW, fontSize: baseFontSize } };
      for (let i = 1; i <= nDays; i++) {
        columnStyles[i] = { cellWidth: colDayW, fontSize: baseFontSize };
      }

      const tableWidth = colLabelW + nDays * colDayW;

      doc.autoTable({
        head: [isoWeekRow, headRow],
        body,
        startY,
        margin: { left: margin, right: margin },
        tableWidth,
        theme: 'plain',
        styles: { fontSize: baseFontSize, cellPadding },
        headStyles: { fillColor: [45, 55, 72], textColor: 255, fontStyle: 'bold', fontSize: 9, cellPadding },
        columnStyles,
        didParseCell: (data) => {
          if (data.section === 'head') {
            data.cell.styles.lineWidth = 0;
            // Riga date (seconda riga header): centra il testo nelle celle giorno
            if (data.row.index === 1 && data.column.index > 0) {
              data.cell.styles.halign = 'center';
              data.cell.styles.valign = 'middle';
            }
          }
          if (data.section === 'body') {
            data.cell.styles.lineWidth = 0;
            const rowType = rowTypes[data.row.index];
            const meta = bodyRowMeta[data.row.index];
            if (rowType === 'areaHeader') {
              data.cell.styles.fillColor = PDF_TABLE_THEME.areaHeader;
              data.cell.styles.textColor = 255;
              data.cell.styles.fontStyle = 'bold';
            } else if (meta?.kind === 'slot') {
              data.cell.styles.fontStyle = 'normal';
              const isLabelCol = data.column.index === 0;
              /** Colonna etichetta slot: come `.grid-row-head` (sfondo tertiary, non il pastello di categoria). */
              const PDF_ROW_HEAD_BG = [36, 52, 71];
              let hlWarm = false;
              let bg;
              if (isLabelCol) {
                bg = [...PDF_ROW_HEAD_BG];
              } else {
                const baseRgb = plannerShiftGridCellRgb(meta);
                bg = [...baseRgb];
                const dayIdx = data.column.index - 1;
                const d = chunkDays[dayIdx];
                if (d && (d.weekday === 'sab' || d.weekday === 'dom' || festiviByDate.has(d.date))) {
                  bg = [
                    Math.min(255, (bg[0] + PDF_TABLE_THEME.weekendBlend[0]) / 2),
                    Math.min(255, (bg[1] + PDF_TABLE_THEME.weekendBlend[1]) / 2),
                    Math.min(255, (bg[2] + PDF_TABLE_THEME.weekendBlend[2]) / 2),
                  ];
                }
                const slots = d
                  ? (slotMapByDate[d.date] || []).filter((s) => getPlannerTableRowLabelKey(s) === meta.labelKey)
                  : [];

                const isChangedCell =
                  Boolean(changedMarkers && slots.length > 0) &&
                  slots.some((s) => pdfSlotMatchesChangedMarkers(s, changedMarkers));
                const isMyShiftCell =
                  Boolean(highlightDoctorId && d) &&
                  slotsAssignedToDoctor(slots, highlightDoctorId, assignmentsByKey);

                if (isChangedCell) {
                  bg = blendRgb(bg, CHANGED_SHIFT_HIGHLIGHT, 0.62);
                  hlWarm = true;
                  data.cell.styles.fontStyle = 'bold';
                  data.cell.styles.lineWidth = { top: 0.55, right: 0.55, bottom: 0.55, left: 0.55 };
                  data.cell.styles.lineColor = [165, 38, 28];
                } else if (isMyShiftCell) {
                  bg = blendRgb(bg, MY_SHIFT_HIGHLIGHT, 0.62);
                  hlWarm = true;
                  data.cell.styles.fontStyle = 'bold';
                  data.cell.styles.lineWidth = { top: 0.35, right: 0, bottom: 0.35, left: 0 };
                  data.cell.styles.lineColor = MY_SHIFT_BORDER;
                }
              }
              data.cell.styles.fillColor = bg;
              data.cell.styles.textColor = isLabelCol ? [231, 236, 239] : plannerPdfTextRgbForFill(bg, hlWarm);
            } else {
              data.cell.styles.fillColor = PDF_TABLE_THEME.defaultCell;
            }
          }
        },
      });

      // Tabella Ore e giornate lavorate per medico/settimana ISO (sistema unificato: M/P 6h, Pr 7,5h, N 12h; rep escluse; assenze accreditate)
      // Notte domenica: 6h+1gg settimana corrente, 6h+1gg settimana successiva. Include prevSunday per spill lun 0-8.
      if (doctors.length > 0) {
        const chunkDates = new Set(chunkDays.map(d => d.date));
        const firstWeekStart = chunkDays[0] ? getWeekStart(chunkDays[0].date) : null;
        const prevSun = firstWeekStart ? addDays(firstWeekStart, -1) : null;
        if (prevSun) chunkDates.add(prevSun);
        let slotsInChunk = slotsFromCalendar.filter(s => chunkDates.has(s.date));
        if (prevSun && !slotsFromCalendar.some(s => s.date === prevSun)) {
          slotsInChunk = [...slotsInChunk, ...getOfficialSlotsForDates([prevSun])];
        }
        const weeklyBreakdownChunk = computeWeeklyBreakdownFromSlots(slotsInChunk);
        const weeksInChunk = [...new Set(chunkDays.map(d => getISOWeekString(d.date)))].sort();
        const weekLabels = weeksInChunk.map(w => w.replace(/^\d{4}-/, '')); // es. "2025-W10" -> "W10"
        const weekRanges = weeksInChunk.map(w => {
          const [y, wn] = w.split('-W');
          const start = getMondayOfISOWeek(parseInt(y, 10), parseInt(wn, 10));
          const endD = new Date(start + 'T12:00:00');
          endD.setDate(endD.getDate() + 6);
          return { w, start, end: endD.toISOString().slice(0, 10) };
        });

        const slotTableStyle = { fontSize: 9, cellPadding: 0.25 };

        const buildSlotRow = (d) => {
          const row = [`${d.sigla} ${d.cognome}`];
          for (const { start, end } of weekRanges) {
            const w = weeklyBreakdownChunk[start]?.[d.id];
            const hours = w?.total ?? 0;
            const days = w?.days ?? 0;
            const credited = getCreditedAbsenceHoursForRange(d.id, start, end) || 0;
            const hasData = hours > 0 || days > 0 || credited > 0;
            const cellStr = hasData
              ? `${Number(hours).toFixed(1)}h\n(${Number(credited).toFixed(1)}h AG)\n(${days} gg)`
              : '';
            row.push(cellStr);
          }
          return row;
        };

        const slotHead = ['Medico', ...weekLabels];
        const slotBody = doctors.map((d) => buildSlotRow(d));

        doc.addPage('a4', 'l');
        const tablesGap = 6;
        const tablePanelW = (contentW - tablesGap) / 2;
        const leftX = margin;
        const rightX = margin + tablePanelW + tablesGap;
        const leftTitleY = margin + 4;
        const rightTitleY = margin + 4;

        const miniGap = 4;
        const miniPanelW = (tablePanelW - miniGap) / 2;

        const highlightWeeklyRow = (data, baseFill, rowDoctors) => {
          if (data.section !== 'body' || !highlightDoctorId) return;
          const drow = rowDoctors[data.row.index];
          if (drow?.id !== highlightDoctorId) return;
          const prev = data.cell.styles.fillColor || baseFill;
          data.cell.styles.fillColor = blendRgb(Array.isArray(prev) ? prev : [255, 255, 255], MY_SHIFT_HIGHLIGHT, 0.38);
          data.cell.styles.fontStyle = 'bold';
        };

        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text('Ore e giornate lavorate per medico e settimana ISO', leftX, leftTitleY);
        const slotStartY = leftTitleY + 5;
        const leftDoctors = doctors.slice(0, Math.ceil(doctors.length / 2));
        const rightDoctors = doctors.slice(Math.ceil(doctors.length / 2));
        const leftSlotBody = leftDoctors.map((d) => buildSlotRow(d));
        const rightSlotBody = rightDoctors.map((d) => buildSlotRow(d));
        const leftSlotLayout = getAdaptiveTableLayout({
          headRow: slotHead,
          bodyRows: leftSlotBody,
          fontSize: 9,
          cellPaddingMm: 0.25,
          availableWidthMm: miniPanelW,
          minWidthByCol: { 0: 16 },
          maxWidthByCol: { 0: 34 },
        });
        const rightSlotLayout = getAdaptiveTableLayout({
          headRow: slotHead,
          bodyRows: rightSlotBody,
          fontSize: 9,
          cellPaddingMm: 0.25,
          availableWidthMm: miniPanelW,
          minWidthByCol: { 0: 16 },
          maxWidthByCol: { 0: 34 },
        });

        doc.autoTable({
          head: [slotHead],
          body: leftSlotBody,
          startY: slotStartY,
          margin: { left: leftX, right: pageW - (leftX + miniPanelW) },
          tableWidth: leftSlotLayout.tableWidth,
          styles: slotTableStyle,
          columnStyles: leftSlotLayout.columnStyles,
          headStyles: { fillColor: [60, 70, 90], textColor: 255, fontStyle: 'bold', cellPadding: 0.25, fontSize: 9 },
          showHead: 'everyPage',
          didParseCell: (data) => highlightWeeklyRow(data, [255, 255, 255], leftDoctors),
        });
        const leftSlotBottom = doc.lastAutoTable.finalY;

        doc.autoTable({
          head: [slotHead],
          body: rightSlotBody,
          startY: slotStartY,
          margin: { left: leftX + miniPanelW + miniGap, right: pageW - (leftX + miniPanelW + miniGap + miniPanelW) },
          tableWidth: rightSlotLayout.tableWidth,
          styles: slotTableStyle,
          columnStyles: rightSlotLayout.columnStyles,
          headStyles: { fillColor: [60, 70, 90], textColor: 255, fontStyle: 'bold', cellPadding: 0.25, fontSize: 9 },
          showHead: 'everyPage',
          didParseCell: (data) => highlightWeeklyRow(data, [255, 255, 255], rightDoctors),
        });
        const rightSlotBottom = doc.lastAutoTable.finalY;
        const slotTableBottom = Math.max(leftSlotBottom, rightSlotBottom);

        // Tabella calendario assenze (stesse settimane dello schema turni)
        const chunkFirstDate = chunkDays[0]?.date;
        const chunkLastDate = chunkDays[chunkDays.length - 1]?.date;
        if (chunkFirstDate && chunkLastDate) {
          const absences = getEffectiveAbsences(chunkFirstDate, chunkLastDate);
          const formatAbsForWeek = (docId, ws, we) => {
            const weekAbs = absences.filter(a => a.doctorId === docId && a.dateStart <= we && (a.dateEnd || a.dateStart) >= ws);
            if (weekAbs.length === 0) return '';
            const skipCat = (c) => c === ABSENCE_CATEGORIES.LIBERA_PROFESSIONE; // LP esclusa (slot-specifica)
            const parts = [];
            for (const a of weekAbs) {
              const abbr = ABSENCE_CATEGORY_ABBR[a.category];
              if (!abbr || skipCat(a.category)) continue;
              const start = a.dateStart?.slice(0, 10) || '';
              const end = (a.dateEnd || a.dateStart)?.slice(0, 10) || start;
              const d1 = start.slice(8, 10), d2 = end.slice(8, 10);
              parts.push(d1 === d2 ? `${parseInt(d1, 10)} ${abbr}` : `${parseInt(d1, 10)}-${parseInt(d2, 10)} ${abbr}`);
            }
            return parts.join(', ');
          };

          const buildAbsRow = (d) => {
            const row = [`${d.sigla} ${d.cognome}`];
            for (const { start, end } of weekRanges) {
              row.push(formatAbsForWeek(d.id, start, end));
            }
            return row;
          };
          const absBody = doctors.map((d) => buildAbsRow(d));
          const absLayout = getAdaptiveTableLayout({
            headRow: slotHead,
            bodyRows: absBody,
            fontSize: 9,
            cellPaddingMm: 0.2,
            availableWidthMm: tablePanelW,
            minWidthByCol: { 0: 20 },
            maxWidthByCol: { 0: 42 },
          });

          const legendH = 7;
          doc.setFontSize(9);
          doc.setFont(undefined, 'bold');
          doc.text('Calendario assenze', rightX, rightTitleY);
          doc.setFontSize(5);
          doc.setTextColor(100);
          doc.text('CO=Congedo ord.  CO (AP)=Congedo anno prec.  PS=Permesso  M=Malattia  F=Formazione  Rx=Rischio  LP=Libera prof.', rightX, rightTitleY + 3);
          doc.setTextColor(0);
          doc.setFontSize(9);
          const absStartY = rightTitleY + legendH;
          doc.autoTable({
            head: [slotHead],
            body: absBody,
            startY: absStartY,
            margin: { left: rightX, right: pageW - (rightX + tablePanelW) },
            tableWidth: absLayout.tableWidth,
            styles: { fontSize: 9, cellPadding: 0.2 },
            columnStyles: absLayout.columnStyles,
            headStyles: { fillColor: [60, 70, 90], textColor: 255, fontStyle: 'bold', cellPadding: 0.2, fontSize: 9 },
            showHead: 'everyPage',
            didParseCell: (data) => highlightWeeklyRow(data, [255, 255, 255], doctors),
          });
          lastSlotTableBottom = Math.max(slotTableBottom, doc.lastAutoTable.finalY);
        } else {
          lastSlotTableBottom = slotTableBottom;
        }
      } else {
        lastSlotTableBottom = doc.lastAutoTable.finalY;
      }
    };

    for (let i = 0; i < dayChunks.length; i++) {
      renderTableForDays(dayChunks[i], i === 0);
    }

    // Ultima pagina: riepilogo ore (sempre sulla prima pagina se una sola, altrimenti nuova pagina)
    let startY;
    if (dayChunks.length > 1) {
      doc.addPage('a4', 'l');
      startY = margin;
    } else {
      startY = (lastSlotTableBottom || doc.lastAutoTable.finalY) + 8;
    }

    // Riepilogo ore per medico (Congedo/Permesso 7,6h/giorno, Formazione, notti 12h). Rep. conteggiata a parte, non in monte orario.
    const firstDate = days[0]?.date || '';
    const lastDate = days[days.length - 1]?.date || '';
    const doctorHours = computeDoctorHours(slotsFromCalendar, assignmentsByKey, firstDate, lastDate, doctors);
    const hoursHead = [['Medico', 'Ore Feriali', 'Ore Weekend', 'Ore Notturne', 'Ore Rep', 'Totale']];
    const hoursBody = doctors.map(d => {
      const h = doctorHours[d.id] || { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0 };
      const tot = h.feriali + h.weekend + h.notturne;
      return [`${d.sigla} ${d.cognome}`, String(h.feriali), String(h.weekend), String(h.notturne), String(h.reperibilita || 0), String(tot)];
    });
    const hoursLayout = getAdaptiveTableLayout({
      headRow: hoursHead[0],
      bodyRows: hoursBody,
      fontSize: 9,
      cellPaddingMm: 1,
      availableWidthMm: contentW,
      minWidthByCol: { 0: 30, 1: 16, 2: 16, 3: 16, 4: 14, 5: 14 },
      maxWidthByCol: { 0: 65 },
    });
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Riepilogo ore di lavoro', margin, startY);
    startY += 6;
    doc.setFont(undefined, 'normal');
    doc.autoTable({
      head: hoursHead,
      body: hoursBody,
      startY,
      margin: { left: margin, right: margin },
      tableWidth: hoursLayout.tableWidth,
      styles: { fontSize: 9, cellPadding: 1 },
      columnStyles: hoursLayout.columnStyles,
      headStyles: { fillColor: [60, 70, 90], textColor: 255, fontStyle: 'bold', cellPadding: 1, fontSize: 9 },
      didParseCell: (data) => {
        if (data.section !== 'body' || !highlightDoctorId) return;
        const drow = doctors[data.row.index];
        if (drow?.id !== highlightDoctorId) return;
        const prev = data.cell.styles.fillColor || [255, 255, 255];
        data.cell.styles.fillColor = blendRgb(Array.isArray(prev) ? prev : [255, 255, 255], MY_SHIFT_HIGHLIGHT, 0.38);
        data.cell.styles.fontStyle = 'bold';
      },
    });

    doc.setFontSize(8);
    doc.setTextColor(120);
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.text(
        `Piano Turni - ${titleRange} - Build: ${buildVersion} - Generato: ${generatedAt} - Pag. ${p}/${totalPages}`,
        pageW / 2,
        pageH - 6,
        { align: 'center' }
      );
    }

    const filename = `${filenameBase}.pdf`;
    let blob = null;
    if (includeBlob) {
      blob = doc.output('blob');
    }
    if (shouldDownload) doc.save(filename);
    return { ok: true, filename, blob, rangeStart: periodStart, rangeEnd: periodEnd };
  } catch (err) {
    console.error('Errore esportazione PDF:', err);
    return { ok: false, error: err.message };
  }
}

/**
 * PDF richieste assenze / desiderata: righe = medici, colonne = giorni del periodo (stesse sigle della vista Assenze).
 */
export async function exportAbsenceDesiderataRequestsPdf({ weekFrom, weekTo }) {
  try {
    const { jsPDF } = await loadJsPDF();
    const state = getState();
    const doctors = (state.doctors ?? []).slice().sort((a, b) => (a.cognome || '').localeCompare(b.cognome || '', 'it'));
    const days = getDaysInWeekRange(weekFrom, weekTo);
    if (!days.length) {
      return { ok: false, error: 'Nessun giorno nel periodo selezionato.' };
    }
    const rangeStart = days[0].date;
    const rangeEnd = days[days.length - 1].date;
    const head = [
      'Medico',
      ...days.map(d => `${d.weekdayShort}\n${String(d.day).padStart(2, '0')}/${d.date.slice(5, 7)}`),
    ];
    const body = doctors.map(doc => {
      const siglaNome = `${doc.sigla || ''} ${doc.cognome || ''}`.trim();
      const cells = days.map(day => {
        const t = getAbsenceCalendarCellLabel(doc.id, day.date, rangeStart, rangeEnd);
        return t || '—';
      });
      return [siglaNome, ...cells];
    });
    const docPdf = new jsPDF({ orientation: 'l', unit: 'mm', format: 'a4' });
    const margin = 8;
    const title = `Richieste assenze e desiderata — ${formatDateIt(rangeStart)} – ${formatDateIt(rangeEnd)}`;
    docPdf.setFontSize(11);
    docPdf.text(title, margin, 12);
    docPdf.setFontSize(7);
    docPdf.setTextColor(80);
    docPdf.text(
      'Sigle come in Assenze: CO, CO (AP), PS, Rx, F, M, LP, NO MPN, NO RG, NO RN…',
      margin,
      17
    );
    docPdf.setTextColor(0);
    docPdf.autoTable({
      head: [head],
      body,
      startY: 20,
      margin: { left: margin, right: margin },
      styles: { fontSize: 5, cellPadding: 0.35, overflow: 'linebreak' },
      headStyles: { fillColor: [60, 70, 90], textColor: 255, fontStyle: 'bold', fontSize: 5 },
      columnStyles: { 0: { cellWidth: 28 } },
      didParseCell: (data) => {
        if (data.section === 'head' && data.column.index > 0) {
          data.cell.styles.halign = 'center';
        }
        if (data.section === 'body' && data.column.index > 0) {
          data.cell.styles.halign = 'center';
        }
      },
    });
    const safeFrom = rangeStart.replace(/-/g, '');
    const safeTo = rangeEnd.replace(/-/g, '');
    docPdf.save(`Richieste-assenze_${safeFrom}_${safeTo}.pdf`);
    return { ok: true, filename: `Richieste-assenze_${safeFrom}_${safeTo}.pdf` };
  } catch (err) {
    console.error('exportAbsenceDesiderataRequestsPdf', err);
    return { ok: false, error: err?.message || String(err) };
  }
}


/** Ore con una cifra decimale, come in Carichi. */
function pdfHours(n) {
  return `${(Math.round((Number(n) || 0) * 10) / 10).toFixed(1)}h`;
}

/** Ore con segno esplicito: nel resoconto il segno del saldo è l'informazione principale. */
function pdfSignedHours(n) {
  const x = Math.round((Number(n) || 0) * 10) / 10;
  return `${x > 0 ? '+' : ''}${x.toFixed(1)}h`;
}

/** Data breve "18 ott" / "31 ott 2026". */
function pdfDayMonth(dateStr, withYear = false) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('it-IT', withYear
    ? { day: 'numeric', month: 'short', year: 'numeric' }
    : { day: 'numeric', month: 'short' });
}

/**
 * Resoconto PDF della card Bimestre di Carichi: stessa fotografia che si vede a schermo, in un
 * foglio che si può allegare a una contestazione o portare in riunione.
 * Usa computeBimesterMirror come la vista, così i due numeri non possono divergere.
 * `doctors` è l'elenco già filtrato dalla vista: al medico esce solo la propria riga.
 */
export async function exportBimesterWorkloadPdf({ year, index, doctors }) {
  try {
    // Il Direttore resta fuori dai conti sul monte orario, come nella pagina.
    const docs = doctorsForWorkloadBalance((doctors ?? []).filter(Boolean));
    if (!docs.length) return { ok: false, error: 'Nessun medico da esportare.' };
    const { jsPDF } = await loadJsPDF();
    const mirror = computeBimesterMirror(year, index, docs);
    const { range, plannedThrough } = mirror;
    const feriali = countFerialiInDateRange(range.start, range.end);
    const gaps = findWorkloadGaps(range.start, range.end, docs);
    const snapshot = getBimesterSnapshots()[range.key] || null;
    const planLabel = plannedThrough ? pdfDayMonth(plannedThrough) : '';

    const rows = docs.map((d) => ({ doc: d, b: mirror.byDoctor[d.id] })).filter((r) => r.b);
    const body = rows.map(({ doc: d, b }) => [
      `${d.sigla || ''} ${d.cognome || ''}`.trim(),
      pdfHours(b.dovute),
      pdfHours(b.turni),
      pdfHours(b.assenze),
      plannedThrough && b.dovuteAlPiano > 0
        ? `${pdfHours(b.coperte)}\n${Math.round((b.coperteAlPiano / b.dovuteAlPiano) * 100)}% a oggi`
        : pdfHours(b.coperte),
      Math.abs(b.saldoApertura) > 0.05 ? pdfSignedHours(b.saldoApertura) : '—',
      plannedThrough ? pdfSignedHours(b.scostamentoAlPiano) : 'n/d',
      pdfSignedHours(b.saldo),
    ]);

    const docPdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const margin = 12;
    docPdf.setFontSize(12.5);
    docPdf.text(`Carichi di lavoro — Bimestre ${range.label}`, margin, 14);
    docPdf.setFontSize(7.5);
    docPdf.setTextColor(90);
    const sub1 = `${pdfDayMonth(range.start)} – ${pdfDayMonth(range.end, true)} · ${feriali} giorni feriali · ${pdfHours(feriali * HOURS_PER_DAY)} a tempo pieno`;
    const sub2 = plannedThrough
      ? `Turni prodotti fino al ${planLabel}: la colonna «A oggi» confronta le ore coperte con il dovuto fino a quella data.`
      : 'Nessun turno ancora prodotto nel bimestre: la colonna «A oggi» non è calcolabile.';
    const sub3 = snapshot
      ? `Bimestre consolidato il ${fmtPdfTimestamp(snapshot.createdAt)}${snapshot.createdBy ? ` da ${snapshot.createdBy}` : ''}: i saldi di chiusura sono congelati.`
      : 'Bimestre non consolidato: i saldi sono ricalcolati in tempo reale e possono ancora cambiare.';
    docPdf.text(sub1, margin, 19.5);
    docPdf.text(sub2, margin, 23);
    docPdf.text(sub3, margin, 26.5);
    docPdf.setTextColor(0);

    docPdf.autoTable({
      head: [['Medico', 'Ore dovute', 'Turni', 'Assenze', 'Coperte', 'Riporto', 'A oggi', 'Saldo bimestre']],
      body,
      startY: 30,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: { top: 0.9, right: 1.2, bottom: 0.9, left: 1.2 }, overflow: 'linebreak' },
      headStyles: { fillColor: [60, 70, 90], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 34 },
        1: { halign: 'right', cellWidth: 17 },
        2: { halign: 'right', cellWidth: 15 },
        3: { halign: 'right', cellWidth: 16 },
        // Coperte: sotto le ore c'è spazio per la barra disegnata in didDrawCell.
        4: { halign: 'right', cellWidth: 38, valign: 'top', minCellHeight: 10 },
        5: { halign: 'right', cellWidth: 15 },
        6: { halign: 'right', cellWidth: 19, fontStyle: 'bold' },
        7: { halign: 'right', fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        if (data.section === 'head' && data.column.index > 0) {
          data.cell.styles.halign = 'right';
        }
        // Rosso solo sul debito: in un foglio stampato il segno meno da solo si perde.
        if (data.section !== 'body') return;
        if (data.column.index === 4) {
          // La seconda riga della cella è la percentuale: più piccola, in grigio.
          data.cell.styles.fontSize = 6.8;
          return;
        }
        if (data.column.index !== 6 && data.column.index !== 7) return;
        const txt = String(data.cell.raw ?? '');
        if (txt.startsWith('-')) data.cell.styles.textColor = [190, 40, 40];
        else if (txt.startsWith('+')) data.cell.styles.textColor = [0, 120, 80];
      },
      didDrawCell: (data) => {
        // Stessa barra delle viste: l'intera larghezza è il monte del bimestre, il riempimento
        // arriva a "dove sei" alla data dei turni prodotti, l'asta a "dove dovresti essere" alla
        // stessa data, e il tratto chiaro sono le ore gia' coperte dopo (ferie gia' registrate).
        if (data.section !== 'body' || data.column.index !== 4) return;
        const row = rows[data.row.index];
        if (!row || !plannedThrough || !(row.b.dovute > 0)) return;
        const clamp = (v) => Math.max(0, Math.min(1, v));
        const oggi = row.b.coperteAlPiano;
        const future = Math.max(0, row.b.coperte - row.b.coperteAlPiano);
        const youFrac = clamp(oggi / row.b.dovute);
        const futureEndFrac = clamp((oggi + future) / row.b.dovute);
        const goalFrac = clamp(row.b.dovuteAlPiano / row.b.dovute);
        const atPace = oggi >= row.b.dovuteAlPiano - 0.05;
        const x = data.cell.x + 2.5;
        const w = data.cell.width - 5;
        const h = 1.6;
        // Sotto le due righe di testo della cella: la barra non deve toccarle.
        const y = data.cell.y + data.cell.height - h - 1.6;
        const youW = w * youFrac;
        docPdf.setFillColor(220, 223, 228);
        docPdf.roundedRect(x, y, w, h, 0.8, 0.8, 'F');
        // Tratto chiaro: stesso colore del riempimento, schiarito, disegnato prima del pieno.
        const futureW = w * (futureEndFrac - youFrac);
        if (futureW > 0.2) {
          if (atPace) docPdf.setFillColor(160, 215, 190);
          else docPdf.setFillColor(170, 195, 235);
          docPdf.roundedRect(x + youW, y, futureW, h, 0.8, 0.8, 'F');
        }
        if (atPace) docPdf.setFillColor(0, 140, 90);
        else docPdf.setFillColor(37, 99, 190);
        if (youW > 0) docPdf.roundedRect(x, y, Math.max(youW, 0.8), h, 0.8, 0.8, 'F');
        docPdf.circle(x + youW, y + h / 2, 1, 'F');
        docPdf.setFillColor(45, 50, 60);
        docPdf.rect(x + w * goalFrac - 0.25, y - 1, 0.5, h + 2, 'F');
      },
    });

    let y = (docPdf.lastAutoTable?.finalY ?? 30) + 5;
    docPdf.setFontSize(6.8);
    docPdf.setTextColor(90);
    const noteLines = [
      'Ore dovute = debito settimanale ÷ 5 per ogni lun–ven non festivo del bimestre (Circolare ISS n. 6 del 3/7/2026: 38h settimanali su base bimestrale).',
      'Coperte = turni assegnati + assenze accreditate (congedo ordinario, permessi, rischio radiologico, malattia, formazione). Le reperibilità restano fuori dal monte orario.',
      'A oggi = coperte + riporto - dovute fino a dove i turni sono stati prodotti: dice se il medico è allineato adesso.',
      'Saldo bimestre = coperte + riporto - dovute sull\'intero bimestre: resta negativo finché le settimane non sono state redatte.',
      'Nella barra sotto le ore coperte il pallino è dove sei e l\'asta dove dovresti essere a oggi: superarla significa essere sopra traiettoria (barra verde). Il tratto chiaro dopo il pallino sono le ore già coperte oltre quella data, per esempio ferie già registrate.',
      'La notte 20-8 conta 4h sul giorno di inizio e 8h sul successivo: a cavallo del bimestre entra solo la quota di competenza.',
    ];
    for (const line of noteLines) {
      const wrapped = docPdf.splitTextToSize(line, 186);
      docPdf.text(wrapped, margin, y);
      y += wrapped.length * 2.9 + 0.5;
    }

    // Gli avvisi sui dati incompleti seguono il foglio: un saldo letto senza è fuorviante.
    const gapSiglas = docs.filter((d) => gaps.byDoctor[d.id]?.length).map((d) => d.sigla);
    const partialSiglas = docs.filter((d) => gaps.partialByDoctor?.[d.id]?.length).map((d) => d.sigla);
    if (gapSiglas.length || partialSiglas.length) {
      y += 2;
      docPdf.setTextColor(190, 40, 40);
      if (gapSiglas.length) {
        const t = docPdf.splitTextToSize(
          `Dato incompleto: ${gapSiglas.join(', ')} ${gapSiglas.length === 1 ? 'ha' : 'hanno'} settimane senza turni né assenze registrate. Le ore dovute risultano più alte del reale finché l'assenza non viene inserita.`,
          186
        );
        docPdf.text(t, margin, y);
        y += t.length * 2.9 + 0.5;
      }
      if (partialSiglas.length) {
        docPdf.setTextColor(150, 110, 20);
        const t = docPdf.splitTextToSize(
          `Da verificare: ${partialSiglas.join(', ')} ${partialSiglas.length === 1 ? 'ha' : 'hanno'} settimane sotto la metà del monte orario senza assenze registrate.`,
          186
        );
        docPdf.text(t, margin, y);
        y += t.length * 2.9 + 0.5;
      }
    }

    const generatedBy = getState().currentUser?.username || '';
    docPdf.setTextColor(140);
    docPdf.setFontSize(6.5);
    const pageHeight = docPdf.internal?.pageSize?.getHeight?.() ?? docPdf.internal?.pageSize?.height ?? 297;
    docPdf.text(
      `Generato il ${fmtPdfTimestamp(new Date().toISOString())}${generatedBy ? ` da ${generatedBy}` : ''}`,
      margin,
      pageHeight - 8
    );

    const filename = `Carichi-bimestre_${range.key}.pdf`;
    docPdf.save(filename);
    return { ok: true, filename };
  } catch (err) {
    console.error('exportBimesterWorkloadPdf', err);
    return { ok: false, error: err?.message || String(err) };
  }
}

/** Data/ora leggibile per intestazioni e piè di pagina del resoconto. */
function fmtPdfTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).endsWith('Z') || String(iso).includes('+') ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
